import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
  Paper,
  Typography,
  Stack,
  Divider,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Collapse,
  Button,
  ButtonGroup,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

function enforceRules(actions) {
  const s = new Set(actions || []);

  // If you can add/update/delete/share, you must be able to view the doc
  const needsDocView = ["doc:add", "doc:update", "doc:delete", "doc:share"].some((k) => s.has(k));
  if (needsDocView) s.add("doc:view");

  // Metadata edit implies metadata view
  if (s.has("meta:edit")) s.add("meta:view");

  return Array.from(s);
}

function sameSet(a, b) {
  const sa = new Set(a);
  const sb = new Set(b);
  if (sa.size !== sb.size) return false;
  for (const x of sa) if (!sb.has(x)) return false;
  return true;
}

function countEnabled(actions) {
  return (actions || []).length;
}

function makeRowKey(folder, name) {
  return `${folder}:${name}`;
}

export default function DocumentTypePermissionsGrid({
  folders,
  documentTypes,
  documentTypePermissions,
  docTypeActionsConfig,
  docTypePresetsConfig,
  presetToActions,
  onChange,
  enablePresets = true,
  folderField = "folder", // set to "type" if your data uses 'type'
  nameField = "name",     // set to "documentType" if needed
}) {
  const [search, setSearch] = useState("");

  // Expand/collapse state per folder
  const [expandedFolders, setExpandedFolders] = useState({});

  // Ensure expanded state includes any new folders as they appear
  useEffect(() => {
    const fs = folders || [];
    setExpandedFolders((prev) => {
      const next = { ...prev };
      for (const f of fs) if (!(f in next)) next[f] = true;
      // optionally remove old folders from state:
      // for (const k of Object.keys(next)) if (!fs.includes(k)) delete next[k];
      return next;
    });
  }, [folders]);

  const setAndBubble = useCallback(
    (next) => {
      onChange?.(next);
    },
    [onChange]
  );

  // Filter document types to only those in assigned folders
  const visibleDocTypes = useMemo(() => {
    const allowed = new Set((folders || []).map(String));
    return (documentTypes || [])
      .map((dt) => ({
        folder: String(dt?.[folderField] ?? "Other"),
        name: String(dt?.[nameField] ?? ""),
        raw: dt,
      }))
      .filter((x) => x.name.length > 0 && allowed.has(x.folder));
  }, [documentTypes, folders, folderField, nameField]);

  // Apply search filter
  const filteredDocTypes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return visibleDocTypes;

    return visibleDocTypes.filter((d) => {
      return d.name.toLowerCase().includes(q) || d.folder.toLowerCase().includes(q);
    });
  }, [visibleDocTypes, search]);

  // Group by folder
  const grouped = useMemo(() => {
    const map = new Map();
    for (const dt of filteredDocTypes) {
      if (!map.has(dt.folder)) map.set(dt.folder, []);
      map.get(dt.folder).push(dt);
    }
    // sort folders and names for stable UI
    const arr = Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
    for (const [, items] of arr) items.sort((a, b) => a.name.localeCompare(b.name));
    return arr; // [ [folder, items[]], ...]
  }, [filteredDocTypes]);

  // Normalize permissions for all visible rows (so missing keys default to [])
  const normalized = useMemo(() => {
    const out = { ...(documentTypePermissions || {}) };

    for (const dt of visibleDocTypes) {
      const key = makeRowKey(dt.folder, dt.name);
      if (!Array.isArray(out[key])) out[key] = [];
      out[key] = enforceRules(out[key]);
    }

    return out;
  }, [documentTypePermissions, visibleDocTypes]);

  const toggleFolder = (folder) => {
    setExpandedFolders((prev) => ({ ...prev, [folder]: !(prev[folder] ?? true) }));
  };

  const hasAction = (rowKey, actionKey) => (normalized[rowKey] || []).includes(actionKey);

  const toggleCell = (rowKey, actionKey) => {
    const current = normalized[rowKey] || [];
    const s = new Set(current);
    if (s.has(actionKey)) s.delete(actionKey);
    else s.add(actionKey);

    setAndBubble({ ...normalized, [rowKey]: enforceRules(Array.from(s)) });
  };

  const setRowAll = (rowKey, enabled) => {
    const nextActions = enabled ? enforceRules(docTypeActionsConfig.map((a) => a.key)) : [];
    setAndBubble({ ...normalized, [rowKey]: nextActions });
  };

  // Column bulk applies to *currently visible rows* (respects folders + search filter)
  const setColumnAll = (actionKey, enabled) => {
    const next = { ...normalized };
    for (const dt of filteredDocTypes) {
      const rowKey = makeRowKey(dt.folder, dt.name);
      const s = new Set(next[rowKey] || []);
      if (enabled) s.add(actionKey);
      else s.delete(actionKey);
      next[rowKey] = enforceRules(Array.from(s));
    }
    setAndBubble(next);
  };

  const setGroupAll = (folder, items, enabled) => {
    const next = { ...normalized };
    for (const dt of items) {
      const rowKey = makeRowKey(folder, dt.name);
      next[rowKey] = enabled ? enforceRules(docTypeActionsConfig.map((a) => a.key)) : [];
    }
    setAndBubble(next);
  };

  const derivePreset = (rowKey) => {
    const current = normalized[rowKey] || [];
    if (sameSet(current, enforceRules(presetToActions("none")))) return "none";
    if (sameSet(current, enforceRules(presetToActions("read")))) return "read";
    if (sameSet(current, enforceRules(presetToActions("write")))) return "write";
    if (sameSet(current, enforceRules(presetToActions("admin")))) return "admin";
    return "custom";
  };

  const applyPreset = (rowKey, preset) => {
    if (preset === "custom") return;
    setAndBubble({ ...normalized, [rowKey]: enforceRules(presetToActions(preset)) });
  };

  const totalCols = 1 + docTypeActionsConfig.length + 1 + (enablePresets ? 1 : 0) + 1;
  // Name + actions + Row + Preset? + Summary (Folder is the group header now)

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack spacing={1}>
        <Typography variant="h6">Document Type Permissions</Typography>
        <Typography variant="body2" color="text.secondary">
          Groups are folders assigned to this group. Each folder shows its document types.
        </Typography>

        <TextField
          size="small"
          placeholder="Search by document type or folder…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 420 }}
        />

        <Divider />

        <Box sx={{ overflowX: "auto" }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 280 }}>Document Type</TableCell>

                  {docTypeActionsConfig.map((a) => (
                    <TableCell
                      key={a.key}
                      align="center"
                      sx={{ fontWeight: 600, whiteSpace: "nowrap", width: 100, py: 1 }}
                    >
                      <Stack direction="column" spacing={0.25} alignItems="center" justifyContent="center">
                        <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                          {a.label}
                        </Typography>
                        <Checkbox
                          size="small"
                          onChange={(e) => setColumnAll(a.key, e.target.checked)}
                          inputProps={{ "aria-label": `Toggle all ${a.label}` }}
                          sx={{ p: 0 }}
                        />
                      </Stack>
                    </TableCell>
                  ))}

                  <TableCell align="center" sx={{ fontWeight: 600, width: 220 }}>
                    Row
                  </TableCell>

                  {enablePresets && (
                    <TableCell align="center" sx={{ fontWeight: 600, width: 160 }}>
                      Preset
                    </TableCell>
                  )}

                  <TableCell align="center" sx={{ fontWeight: 600, width: 120 }}>
                    Summary
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {grouped.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={totalCols}>
                      <Typography variant="body2" color="text.secondary">
                        No document types for selected folders.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {grouped.map(([folder, items]) => {
                  const isOpen = expandedFolders[folder] ?? true;

                  return (
                    <React.Fragment key={folder}>
                      {/* Group header row */}
                      <TableRow>
                        <TableCell colSpan={totalCols} sx={{ bgcolor: "background.default" }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <IconButton size="small" onClick={() => toggleFolder(folder)}>
                                {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>

                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {folder}
                              </Typography>

                              <Chip size="small" variant="outlined" label={`${items.length}`} />
                            </Stack>

                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setGroupAll(folder, items, true)}
                              >
                                All in folder
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setGroupAll(folder, items, false)}
                              >
                                None in folder
                              </Button>
                            </Stack>
                          </Stack>
                        </TableCell>
                      </TableRow>

                      {/* Collapsible content */}
                      <TableRow>
                        <TableCell colSpan={totalCols} sx={{ p: 0, borderBottom: "none" }}>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Table size="small">
                              <TableBody>
                                {items.map((dt) => {
                                  const rowKey = makeRowKey(folder, dt.name);
                                  const actions = normalized[rowKey] || [];

                                  return (
                                    <TableRow key={rowKey} hover>
                                      <TableCell sx={{ width: 280 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {dt.name}
                                        </Typography>
                                      </TableCell>

                                      {docTypeActionsConfig.map((a) => (
                                        <TableCell key={a.key} align="center" sx={{ width: 100, py: 0.5 }}>
                                          <Checkbox
                                            size="small"
                                            checked={hasAction(rowKey, a.key)}
                                            onChange={() => toggleCell(rowKey, a.key)}
                                            sx={{ p: 0 }}
                                          />
                                        </TableCell>
                                      ))}

                                      <TableCell align="center" sx={{ width: 220 }}>
                                        <ButtonGroup size="small" variant="outlined">
                                          <Button onClick={() => setRowAll(rowKey, true)}>All</Button>
                                          <Button onClick={() => setRowAll(rowKey, false)}>None</Button>
                                        </ButtonGroup>
                                      </TableCell>

                                      {enablePresets && (
                                        <TableCell align="center" sx={{ width: 160 }}>
                                          <FormControl size="small" fullWidth>
                                            <Select
                                              value={derivePreset(rowKey)}
                                              onChange={(e) => applyPreset(rowKey, e.target.value)}
                                            >
                                              {docTypePresetsConfig.map((p) => (
                                                <MenuItem key={p.value} value={p.value}>
                                                  {p.label}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </TableCell>
                                      )}

                                      <TableCell align="center" sx={{ width: 120 }}>
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          label={`${countEnabled(actions)} perms`}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Typography variant="caption" color="text.secondary">
          Rules enforced: meta:edit implies meta:view; add/update/delete/share imply doc:view.
        </Typography>
      </Stack>
    </Paper>
  );
}
